import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { SubTask, Task } from "../../types";

/**
 * Returns a `reorderSubtasks(taskId, orderedSubtasks)` that optimistically
 * rewrites one task's subtask ordering in the tasks cache — reflected instantly
 * in every consumer of `useTasks` (e.g. the open task dialog) — then persists
 * the new `order_position` on each row and rolls back on failure. Mirrors
 * {@link useMoveSubtask}, scoped to reordering a single task's subtask list.
 *
 * Each subtask is assigned `order_position = (index + 1) * 100`, matching
 * the pre-migration reorderSubtasks. The per-row writes fan out in parallel; if any
 * fails it restores the pre-mutation snapshot, captures the error to Sentry,
 * surfaces a toast, and returns `false`. No Google Calendar sync fires on a
 * reorder — position never changes an event — matching the god-hook original.
 *
 * This is the query-cache-aware replacement for the pre-migration reorder.
 * Unlike that original, which applied the optimistic update but left it in
 * place on a failed write, this hook rolls the cache back and reports the
 * error — the path Cypress cannot reach because it stubs the update at the
 * network boundary.
 */
export function useReorderSubtasks() {
  const queryClient = useQueryClient();

  return async (
    taskId: string,
    orderedSubtasks: SubTask[],
  ): Promise<boolean> => {
    const withPositions = orderedSubtasks.map((st, i) => ({
      ...st,
      order_position: (i + 1) * 100,
    }));

    // Snapshot before mutating so a failed write can be rolled back.
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

    // Optimistic reorder — reflected instantly in every consumer of useTasks.
    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((t) =>
            t.id === taskId ? { ...t, subtasks: withPositions } : t,
          )
        : prev,
    );

    // Persist each new position in parallel.
    const results = await Promise.all(
      withPositions.map((st) =>
        supabase
          .from("subtasks")
          .update({
            order_position: st.order_position,
            updated_at: new Date().toISOString(),
          })
          .eq("id", st.id),
      ),
    );

    const failed = results.find((r) => r.error);
    if (failed) {
      // Restore the whole snapshot if any write fails.
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(failed.error, {
        extra: {
          mutation: "useReorderSubtasks",
          taskId,
          subtaskIds: withPositions.map((st) => st.id),
        },
      });
      toast.error("Failed to reorder subtasks. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
